import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import './Pagination.css';

import { ReactComponent as ArrowDown } from '../../icons/arrow-down.svg';

function Pagination({
  reduxSelector,
  reduxAction,
  currentPage: currentPageProp,
  onPageChange,
  totalPages,
  maxVisibleButtons = 5,
  pageSize: pageSizeProp,
  reduxPageSizeSelector,
  reduxPageSizeAction,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  showPageSize = true,
}) {
  const dispatch = useDispatch();
  const reduxCurrentPage = useSelector(typeof reduxSelector === 'function' ? reduxSelector : () => undefined);
  const reduxPageSize = useSelector(typeof reduxPageSizeSelector === 'function' ? reduxPageSizeSelector : () => undefined);

  const currentPage = reduxCurrentPage ?? currentPageProp ?? 1;
  const resolvedPageSize = reduxPageSize ?? pageSizeProp ?? (Array.isArray(pageSizeOptions) && pageSizeOptions.length > 0 ? (pageSizeOptions.includes(25) ? 25 : pageSizeOptions[0]) : 25);

  const [isModalVisible, setIsModalVisible] = useState(false); // Czy modal strony jest widoczny
  const [inputValue, setInputValue] = useState(''); // Wartość wpisana w pole
  const [isPageSizeDropdownOpen, setIsPageSizeDropdownOpen] = useState(false); // Czy dropdown liczby elementów jest otwarty
  const pageSizeDropdownRef = useRef(null);

  // Zamykanie dropdownu rozmiaru strony po kliknięciu na zewnątrz
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pageSizeDropdownRef.current && !pageSizeDropdownRef.current.contains(event.target)) {
        setIsPageSizeDropdownOpen(false);
      }
    };

    if (isPageSizeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPageSizeDropdownOpen]);

  // Funkcja do zmiany strony
  const changePage = (page) => {
    if (page >= 1 && page <= totalPages) {
      if (typeof reduxAction === 'function') {
        dispatch(reduxAction(page)); // Aktualizacja bieżącej strony w Redux
      }
      if (typeof onPageChange === 'function') {
        onPageChange(page);
      }
    }
  };

  // Funkcja do zmiany liczby elementów na stronę
  const changePageSize = (size) => {
    const sizeNumber = parseInt(size, 10);
    if (!Number.isFinite(sizeNumber)) return;

    if (typeof reduxPageSizeAction === 'function') {
      dispatch(reduxPageSizeAction(sizeNumber));
    }
    if (typeof onPageSizeChange === 'function') {
      onPageSizeChange(sizeNumber);
    }
    if (typeof reduxAction === 'function' && typeof reduxPageSizeAction !== 'function') {
      dispatch(reduxAction(1));
    }
    if (typeof onPageChange === 'function' && typeof onPageSizeChange !== 'function') {
      onPageChange(1);
    }
    setIsPageSizeDropdownOpen(false);
  };

  // Generowanie zakresu stron z obsługą "..."
  const generatePageButtons = () => {
    const buttons = [];
    const half = Math.floor(maxVisibleButtons / 2);

    if (totalPages <= maxVisibleButtons) {
      for (let i = 1; i <= totalPages; i++) {
        buttons.push(i);
      }
    } else {
      if (currentPage <= half + 1) {
        for (let i = 1; i <= maxVisibleButtons - 1; i++) {
          buttons.push(i);
        }
        buttons.push('...');
        buttons.push(totalPages);
      } else if (currentPage >= totalPages - half) {
        buttons.push(1);
        buttons.push('...');
        for (let i = totalPages - (maxVisibleButtons - 2); i <= totalPages; i++) {
          buttons.push(i);
        }
      } else {
        buttons.push(1);
        buttons.push('...');
        for (let i = currentPage - half + 1; i <= currentPage + half - 1; i++) {
          buttons.push(i);
        }
        buttons.push('...');
        buttons.push(totalPages);
      }
    }

    return buttons;
  };

  const pageButtons = generatePageButtons();

  // Obsługa zatwierdzenia numeru strony
  const handleInputSubmit = () => {
    const page = parseInt(inputValue, 10);
    if (!isNaN(page)) {
      changePage(page);
    }
    setIsModalVisible(false); // Ukryj modal po zatwierdzeniu
    setInputValue(''); // Wyczyść pole
  };

  return (
    <div className="pagination">
      <button
        className="pagination-button"
        disabled={currentPage === 1}
        onClick={() => changePage(currentPage - 1)}
      >
        &#10094;
      </button>
      {pageButtons.map((button, index) =>
        button === '...' ? (
          <span key={index} className="pagination-ellipsis">
            ...
          </span>
        ) : (
          <button 
            key={index}
            className={`pagination-button ${button === currentPage ? 'active' : ''}`}
            onClick={() => changePage(button)}
          >
            {button}
          </button>
        )
      )}
      <button
        className="pagination-button"
        disabled={currentPage === totalPages}
        onClick={() => changePage(currentPage + 1)}
      >
        &#10095;
      </button>

      {/* Przycisk ze skokiem do wybranej strony */}
      <button
        className="pagination-button pagination-button-separated"
        onClick={() => setIsModalVisible(true)}
      >
        Strona <ArrowDown />
      </button>

      {/* Dropdown wyboru liczby elementów na stronę */}
      {showPageSize && Array.isArray(pageSizeOptions) && pageSizeOptions.length > 0 && (
        <div className="pagination-page-size-container" ref={pageSizeDropdownRef}>
          <button
            type="button"
            className={`pagination-button pagination-button-separated pagination-page-size-btn ${isPageSizeDropdownOpen ? 'active' : ''}`}
            onClick={() => setIsPageSizeDropdownOpen((prev) => !prev)}
            aria-haspopup="listbox"
            aria-expanded={isPageSizeDropdownOpen}
            title="Wybierz liczbę elementów na stronę"
          >
            <span>{resolvedPageSize} / str.</span>
            <ArrowDown className={`pagination-page-size-arrow ${isPageSizeDropdownOpen ? 'open' : ''}`} />
          </button>

          {isPageSizeDropdownOpen && (
            <ul
              className="pagination-page-size-dropdown"
              role="listbox"
              aria-label="Liczba elementów na stronę"
            >
              {pageSizeOptions.map((option) => (
                <li
                  key={option}
                  role="option"
                  aria-selected={option === resolvedPageSize}
                  className={`pagination-page-size-item ${option === resolvedPageSize ? 'active' : ''}`}
                  onClick={() => changePageSize(option)}
                >
                  {option} na stronę
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Modal */}
      {isModalVisible && (
        <div className="pagination-modal">
          <div className="pagination-modal-content">
            <h3>Wybierz stronę</h3>
            <input
              type="number"
              className="pagination-input"
              placeholder="Nr strony"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleInputSubmit()}
            />
            <div className="pagination-modal-buttons">
              <button className="pagination-submit" onClick={handleInputSubmit}>
                OK
              </button>
              <button
                className="pagination-cancel"
                onClick={() => setIsModalVisible(false)}
              >
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

Pagination.propTypes = {
  reduxSelector: PropTypes.func, // Funkcja selektora Redux do pobrania bieżącej strony
  reduxAction: PropTypes.func, // Akcja Redux do zmiany strony
  currentPage: PropTypes.number,
  onPageChange: PropTypes.func,
  totalPages: PropTypes.number.isRequired, // Całkowita liczba stron
  maxVisibleButtons: PropTypes.number, // Maksymalna liczba widocznych przycisków (domyślnie 5)
  pageSize: PropTypes.number,
  reduxPageSizeSelector: PropTypes.func,
  reduxPageSizeAction: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  showPageSize: PropTypes.bool,
};

export default Pagination;